import React, { useMemo } from 'react';
import {
  Stack,
  TextField,
  Autocomplete,
  MenuItem,
  Typography,
  Box,
  Divider,
  Chip,
  CircularProgress,
  InputAdornment,
} from '@mui/material';

import PhiHintBanner from './PhiHintBanner';

const NPI_MONO_SX = {
  fontFamily: 'Roboto Mono, ui-monospace, SFMono-Regular, monospace',
};

const ComposeForm = ({
  form,
  setField,
  patients,
  patientsLoading,
  patientQuery,
  onPatientQueryChange,
  documents,
  documentsLoading,
  fieldErrors,
  phiResult,
  readOnly,
  subjectRef,
  bodyRef,
}) => {
  const selectedPatient = useMemo(
    () => patients.find((p) => p.id === form.patient) || null,
    [patients, form.patient],
  );

  const selectedDocs = useMemo(
    () => documents.filter((d) => form.document_ids.includes(d.id)),
    [documents, form.document_ids],
  );

  return (
    <Stack spacing={2.5}>
      <Box>
        <SectionLabel>Patient & provider</SectionLabel>
        <Stack spacing={2}>
          <Autocomplete
            options={patients}
            value={selectedPatient}
            disabled={readOnly}
            loading={patientsLoading}
            // Server-side filtering: return options as-is. The backend already
            // narrowed the list via /patients/?search=<q>.
            filterOptions={(x) => x}
            inputValue={patientQuery ?? ''}
            onInputChange={(_, val, reason) => {
              if (reason === 'reset') return;
              onPatientQueryChange?.(val);
            }}
            onChange={(_, val) => setField('patient', val?.id || '')}
            getOptionLabel={(opt) => {
              if (!opt) return '';
              const first = opt.first_name || '';
              const last = opt.last_name || '';
              const name = `${first} ${last}`.trim();
              return name || opt.name || opt.email || opt.id || '';
            }}
            isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
            noOptionsText={
              (patientQuery || '').trim().length < 2
                ? 'Type at least 2 characters to search.'
                : 'No patients match that search.'
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Patient"
                required
                size="small"
                error={!!fieldErrors.patient}
                helperText={fieldErrors.patient || 'Search by patient name (min. 2 characters).'}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {patientsLoading ? (
                        <InputAdornment position="end">
                          <CircularProgress size={16} aria-label="Searching patients" />
                        </InputAdornment>
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Provider name"
              value={form.provider_name}
              onChange={(e) => setField('provider_name', e.target.value)}
              required
              size="small"
              fullWidth
              disabled={readOnly}
              error={!!fieldErrors.provider_name}
              helperText={fieldErrors.provider_name || ' '}
            />
            <TextField
              label="Provider NPI"
              value={form.provider_npi}
              onChange={(e) => setField('provider_npi', e.target.value.replace(/\D/g, '').slice(0, 10))}
              required
              size="small"
              fullWidth
              disabled={readOnly}
              error={!!fieldErrors.provider_npi}
              helperText={fieldErrors.provider_npi || '10 digits, numeric only.'}
              inputProps={{ inputMode: 'numeric', pattern: '\\d{10}', style: NPI_MONO_SX }}
            />
          </Stack>
          <TextField
            label="Provider email"
            type="email"
            value={form.provider_email}
            onChange={(e) => setField('provider_email', e.target.value)}
            required
            size="small"
            fullWidth
            disabled={readOnly}
            error={!!fieldErrors.provider_email}
            helperText={fieldErrors.provider_email || 'Where the secure link is sent.'}
          />
        </Stack>
      </Box>

      <Divider />

      <Box>
        <SectionLabel>Identity check</SectionLabel>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            select
            label="Identity check mode"
            value={form.identity_check_mode}
            onChange={(e) => setField('identity_check_mode', e.target.value)}
            size="small"
            fullWidth
            disabled={readOnly}
            helperText={
              form.identity_check_mode === 'npi_last4'
                ? 'Provider must enter the last 4 of their NPI to open the link.'
                : 'A one-time magic link is emailed; no second factor.'
            }
          >
            <MenuItem value="npi_last4">NPI last 4 (recommended)</MenuItem>
            <MenuItem value="magic_link">Magic link only</MenuItem>
          </TextField>
          {form.identity_check_mode === 'npi_last4' && (
            <TextField
              label="NPI last 4"
              value={form.npi_last4}
              onChange={(e) => setField('npi_last4', e.target.value.replace(/\D/g, '').slice(0, 4))}
              required
              size="small"
              fullWidth
              disabled={readOnly}
              error={!!fieldErrors.npi_last4}
              helperText={fieldErrors.npi_last4 || 'Auto-filled from the provider NPI.'}
              inputProps={{ inputMode: 'numeric', pattern: '\\d{4}', style: NPI_MONO_SX }}
            />
          )}
          <TextField
            label="Expires in (days)"
            type="number"
            value={form.expires_in_days}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              setField('expires_in_days', Number.isFinite(v) ? Math.min(30, Math.max(1, v)) : 7);
            }}
            size="small"
            fullWidth
            disabled={readOnly}
            inputProps={{ min: 1, max: 30 }}
            error={!!fieldErrors.expires_in_days}
            helperText={fieldErrors.expires_in_days || '1–30. Default 7.'}
          />
        </Stack>
      </Box>

      <Divider />

      <Box>
        <SectionLabel>Email content</SectionLabel>
        <Stack spacing={2}>
          <TextField
            label="Subject"
            value={form.email_subject}
            onChange={(e) => setField('email_subject', e.target.value)}
            required
            size="small"
            fullWidth
            disabled={readOnly}
            error={!!fieldErrors.email_subject}
            helperText={fieldErrors.email_subject || ' '}
            inputRef={subjectRef}
          />
          <TextField
            label="Body"
            value={form.email_body}
            onChange={(e) => setField('email_body', e.target.value)}
            required
            multiline
            minRows={8}
            size="small"
            fullWidth
            disabled={readOnly}
            error={!!fieldErrors.email_body}
            helperText={fieldErrors.email_body || 'Keep this PHI-free. The secure link is appended server-side.'}
            inputRef={bodyRef}
          />
          {phiResult?.flagged && <PhiHintBanner hits={phiResult.hits} />}
        </Stack>
      </Box>

      <Divider />

      <Box>
        <SectionLabel>Documents</SectionLabel>
        <Autocomplete
          multiple
          options={documents}
          value={selectedDocs}
          disabled={readOnly || !form.patient}
          loading={documentsLoading}
          onChange={(_, vals) => setField('document_ids', vals.map((v) => v.id))}
          getOptionLabel={(opt) => opt?.filename || opt?.name || opt?.id || ''}
          isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
          renderTags={(value, getTagProps) =>
            value.map((opt, idx) => (
              <Chip
                {...getTagProps({ index: idx })}
                key={opt.id}
                label={opt.filename || opt.name || opt.id}
                size="small"
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={form.patient ? 'Documents to attach' : 'Select a patient first'}
              size="small"
              error={!!fieldErrors.document_ids}
              helperText={fieldErrors.document_ids || 'Documents are delivered behind the secure link, not in the email body.'}
            />
          )}
        />
      </Box>
    </Stack>
  );
};

const SectionLabel = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      display: 'block',
      mb: 1.5,
      fontWeight: 600,
      color: 'text.secondary',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    }}
  >
    {children}
  </Typography>
);

export default ComposeForm;
